import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Plus, Trash, Key, Power, X } from "@phosphor-icons/react";

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newUser, setNewUser] = useState({ username: "", password: "", role: "member", display_name: "" });
  const [resetUser, setResetUser] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    const r = await api.get("/users");
    setUsers(r.data || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.post("/users", newUser);
      setShowAdd(false);
      setNewUser({ username: "", password: "", role: "member", display_name: "" });
      load();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed");
    }
  };

  const toggle = async (id) => {
    await api.post(`/users/${id}/toggle-active`);
    load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    await api.delete(`/users/${id}`);
    load();
  };

  const reset = async (e) => {
    e.preventDefault();
    await api.post(`/users/${resetUser.id}/reset-password`, { new_password: newPassword });
    setResetUser(null);
    setNewPassword("");
  };

  const setMode = async (id, mode) => {
    try {
      await api.post(`/users/${id}/meta-mode`, { mode });
      load();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to update mode");
    }
  };

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="h-14 border-b border-zinc-900 px-6 flex items-center backdrop-blur-xl bg-zinc-950/70 sticky top-0 z-10">
        <div>
          <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">// Users</div>
          <div className="text-sm font-medium">Team Members ({users.length})</div>
        </div>
        <button
          data-testid="add-user-button"
          onClick={() => setShowAdd(true)}
          className="ml-auto bg-orange-500 hover:bg-orange-600 text-zinc-950 text-xs font-semibold px-3 py-1.5 flex items-center gap-1.5 transition-colors"
        >
          <Plus size={12} weight="bold" /> Add User
        </button>
      </div>

      <div className="p-6">
        <div className="bg-zinc-900/40 border border-zinc-800/60">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] font-mono tracking-[0.15em] uppercase text-zinc-500">
                <th className="text-left px-4 py-2.5 font-medium">Username</th>
                <th className="text-left px-3 py-2.5 font-medium">Display</th>
                <th className="text-left px-3 py-2.5 font-medium">Role</th>
                <th className="text-left px-3 py-2.5 font-medium">Meta Mode</th>
                <th className="text-left px-3 py-2.5 font-medium">Status</th>
                <th className="text-left px-3 py-2.5 font-medium">Created</th>
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-t border-zinc-800/40 hover:bg-zinc-900/40" data-testid={`user-row-${u.username}`}>
                  <td className="px-4 py-3 font-medium">{u.username}</td>
                  <td className="px-3 py-3 text-zinc-400">{u.display_name}</td>
                  <td className="px-3 py-3">
                    <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 ${
                      u.role === "admin" ? "bg-orange-500/15 text-orange-400" : "bg-zinc-800 text-zinc-400"
                    }`}>
                      {u.role}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <div className="inline-flex border border-zinc-800 text-[10px] font-mono uppercase tracking-wider">
                      <button
                        data-testid={`mode-mock-${u.username}`}
                        onClick={() => setMode(u.id, "mock")}
                        className={`px-2 py-0.5 transition-colors ${(u.meta_mode_override || "mock") === "mock" ? "bg-zinc-800 text-zinc-200" : "text-zinc-500 hover:text-zinc-300"}`}
                      >
                        Mock
                      </button>
                      <button
                        data-testid={`mode-live-${u.username}`}
                        onClick={() => setMode(u.id, "live")}
                        className={`px-2 py-0.5 transition-colors border-l border-zinc-800 ${u.meta_mode_override === "live" ? "bg-orange-500/15 text-orange-400" : "text-zinc-500 hover:text-zinc-300"}`}
                      >
                        Live
                      </button>
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 ${
                      u.is_active ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"
                    }`}>
                      {u.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-3 py-3 font-mono text-[11px] text-zinc-500">{u.created_at?.slice(0, 10)}</td>
                  <td className="px-4 py-3 text-right space-x-1">
                    <button data-testid={`reset-${u.username}`} onClick={() => setResetUser(u)} className="p-1.5 hover:bg-zinc-800" title="Reset password">
                      <Key size={12} />
                    </button>
                    <button data-testid={`toggle-${u.username}`} onClick={() => toggle(u.id)} className="p-1.5 hover:bg-zinc-800" title="Toggle active" disabled={u.id === me?.id}>
                      <Power size={12} className={u.is_active ? "text-emerald-400" : "text-red-400"} />
                    </button>
                    <button data-testid={`delete-${u.username}`} onClick={() => del(u.id)} className="p-1.5 hover:bg-zinc-800 text-red-400" title="Delete" disabled={u.id === me?.id}>
                      <Trash size={12} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add modal */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <form onSubmit={create} className="bg-[#0A0A0A] border border-zinc-800 w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500 mb-1">// New User</div>
                <h3 className="font-display text-lg font-semibold">Create Team Member</h3>
              </div>
              <button type="button" onClick={() => setShowAdd(false)} className="text-zinc-500 hover:text-zinc-200"><X size={16} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 block mb-1">Username</label>
                <input data-testid="new-user-username" required value={newUser.username} onChange={(e) => setNewUser({...newUser, username: e.target.value})} className="w-full bg-[#050505] border border-zinc-800 px-2.5 py-2 text-sm focus:outline-none focus:border-orange-500" />
              </div>
              <div>
                <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 block mb-1">Display name</label>
                <input data-testid="new-user-display" value={newUser.display_name} onChange={(e) => setNewUser({...newUser, display_name: e.target.value})} className="w-full bg-[#050505] border border-zinc-800 px-2.5 py-2 text-sm focus:outline-none focus:border-orange-500" />
              </div>
              <div>
                <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 block mb-1">Password</label>
                <input data-testid="new-user-password" required type="password" value={newUser.password} onChange={(e) => setNewUser({...newUser, password: e.target.value})} className="w-full bg-[#050505] border border-zinc-800 px-2.5 py-2 text-sm focus:outline-none focus:border-orange-500" />
              </div>
              <div>
                <label className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 block mb-1">Role</label>
                <select data-testid="new-user-role" value={newUser.role} onChange={(e) => setNewUser({...newUser, role: e.target.value})} className="w-full bg-[#050505] border border-zinc-800 px-2.5 py-2 text-sm focus:outline-none focus:border-orange-500">
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              {error && <div className="text-red-400 text-xs font-mono">{error}</div>}
            </div>
            <div className="mt-5 flex gap-2 justify-end">
              <button type="button" onClick={() => setShowAdd(false)} className="px-3 py-2 text-xs text-zinc-400 hover:text-zinc-100">Cancel</button>
              <button data-testid="create-user-submit" type="submit" className="bg-orange-500 hover:bg-orange-600 text-zinc-950 font-semibold text-xs px-4 py-2">Create</button>
            </div>
          </form>
        </div>
      )}

      {/* Reset password modal */}
      {resetUser && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <form onSubmit={reset} className="bg-[#0A0A0A] border border-zinc-800 w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-semibold">Reset password for <span className="text-orange-500">{resetUser.username}</span></h3>
              <button type="button" onClick={() => setResetUser(null)} className="text-zinc-500"><X size={16} /></button>
            </div>
            <input
              data-testid="reset-password-input"
              required
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="New password"
              className="w-full bg-[#050505] border border-zinc-800 px-2.5 py-2 text-sm focus:outline-none focus:border-orange-500"
            />
            <div className="mt-5 flex gap-2 justify-end">
              <button type="button" onClick={() => setResetUser(null)} className="px-3 py-2 text-xs text-zinc-400">Cancel</button>
              <button data-testid="reset-password-submit" type="submit" className="bg-orange-500 hover:bg-orange-600 text-zinc-950 font-semibold text-xs px-4 py-2">Reset</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
